import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from './ui/alert-dialog';
import { Plus, Trash2, Loader2, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const todayIso = () => new Date().toISOString().slice(0, 10);
const eur = (n) => (Number(n) || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s + (s.length === 10 ? 'T00:00:00' : ''));
  return isNaN(d) ? s : d.toLocaleDateString('fr-FR');
};

const EMPTY = { employe_id: '', montant: '', date: todayIso(), motif: '' };

/**
 * Onglet "Avances sur paye" : enregistrer une avance (employé, montant, date,
 * motif) et consulter l'historique. Réservé admin/manager.
 */
export function AvancesTab({ employes = [], api, isAdmin = true }) {
  const [avances, setAvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterEmp, setFilterEmp] = useState('all');
  const [form, setForm] = useState(EMPTY);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/rh/avances');
      setAvances(res.data || []);
    } catch {
      toast.error('Erreur chargement des avances');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setForm({ ...EMPTY, date: todayIso() }); setShowDialog(true); };

  const save = async () => {
    if (!form.employe_id) { toast.error('Sélectionnez un employé'); return; }
    const montant = parseFloat(String(form.montant).replace(',', '.'));
    if (!montant || montant <= 0) { toast.error('Montant invalide'); return; }
    if (!form.date) { toast.error('Sélectionnez une date'); return; }
    setSaving(true);
    try {
      await api.post('/rh/avances', {
        employe_id: form.employe_id,
        montant,
        date: form.date,
        motif: form.motif || '',
      });
      toast.success('Avance enregistrée');
      setShowDialog(false);
      setForm(EMPTY);
      fetchAll();
    } catch (e) {
      toast.error('Erreur : ' + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/rh/avances/${id}`);
      toast.success('Avance supprimée');
      fetchAll();
    } catch (e) {
      toast.error('Erreur : ' + (e.response?.data?.detail || e.message));
    }
  };

  const filtered = useMemo(() => (
    filterEmp === 'all' ? avances : avances.filter((a) => a.employe_id === filterEmp)
  ), [avances, filterEmp]);

  const total = useMemo(() => filtered.reduce((s, a) => s + (Number(a.montant) || 0), 0), [filtered]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Wallet className="w-5 h-5" /> Avances sur paye
        </CardTitle>
        <div className="flex items-center gap-2">
          <Select value={filterEmp} onValueChange={setFilterEmp}>
            <SelectTrigger className="w-52" data-testid="avance-filter-employe">
              <SelectValue placeholder="Tous les employés" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les employés</SelectItem>
              {employes.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button onClick={openNew} className="gap-1.5" data-testid="avance-new">
              <Plus className="w-4 h-4" /> Nouvelle avance
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Aucune avance enregistrée.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Employé</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead>Motif</TableHead>
                  {isAdmin && <TableHead className="w-10"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="whitespace-nowrap">{fmtDate(a.date)}</TableCell>
                    <TableCell className="font-medium">{a.employe_nom}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">{eur(a.montant)}</TableCell>
                    <TableCell className="text-muted-foreground">{a.motif || '—'}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid="avance-delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cette avance ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {a.employe_nom} — {eur(a.montant)} du {fmtDate(a.date)}. Cette action est définitive.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(a.id)}>Supprimer</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-end pt-3 pr-1 text-sm">
              <span className="text-muted-foreground mr-2">Total :</span>
              <span className="font-semibold tabular-nums">{eur(total)}</span>
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle avance sur paye</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Employé *</Label>
              <Select value={form.employe_id} onValueChange={(v) => setForm({ ...form, employe_id: v })}>
                <SelectTrigger data-testid="avance-form-employe">
                  <SelectValue placeholder="Choisir un employé" />
                </SelectTrigger>
                <SelectContent>
                  {employes.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Montant (€) *</Label>
                <Input
                  type="number" min="0" step="0.01" inputMode="decimal"
                  value={form.montant}
                  onChange={(e) => setForm({ ...form, montant: e.target.value })}
                  placeholder="0,00"
                  data-testid="avance-form-montant"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="avance-form-date"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Motif</Label>
              <Textarea
                value={form.motif}
                onChange={(e) => setForm({ ...form, motif: e.target.value })}
                placeholder="Motif de l'avance (facultatif)"
                rows={2}
                data-testid="avance-form-motif"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="avance-form-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
